import React, { useContext, useEffect, useState } from 'react';
import { Button, Dropdown, ListGroup } from 'react-bootstrap';
import { Check2, ChevronDown, ChevronUp, Pin, Plus, X } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import TextInput from '../../../../form-generator/components/TextInput';
import { STATUS_INTEREST_IN_MY_COURSE, STATUS_FREE_IRR_CALC } from '../../../../helpers/constants';
import RelatedApartmentStatus from '../../../appartment/RelatedApartmentStatus';
import SearchableFilterDropdown from './SearchableFilterDropdown';

const CustomerItem = ({
  customer: c,
  active,
  onSelect,
  translate,
  archived,
  onMatchingProfileClick,
  onMatchingProfileMouseEnter,
  onMatchingProfileMouseLeave,
  activeMatchingProfileId,
  selectedMatchingCustomers,
  onSelectMatchingCustomers,
  showProfiles
}) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    setSelectedProfile(c?.selectedProfile || null);
  }, [c?.selectedProfile]);

  let colorCodeBasedOnLabel = '';
  const labelParts = [];
  if (c.isCreator) {
    labelParts.push('A');
    colorCodeBasedOnLabel = '#198754';
  }
  if (c.isModifier) {
    labelParts.push('M');
    colorCodeBasedOnLabel = '#ffc107';
  }
  if (c.isDeleter) {
    labelParts.push('D');
    colorCodeBasedOnLabel = '#dc3545';
  }
  const label = labelParts.join(', ');
  return (
    <ListGroup.Item
      style={{
        fontSize: 14,
        borderLeft: ` ${colorCodeBasedOnLabel ? `3px solid ${colorCodeBasedOnLabel}` : 'none'}`
      }}
      className={`py-1 m-0 d-flex justify-content-between align-items-center ${
        active ? 'bg-primary' : archived ? 'bg-light-gray hover-list-group-item' : ''
      } `}
      action
      active={active}
      key={c['_id']}
    >
      <div style={{ flex: 1, padding: '0.25rem 0' }} onClick={() => onSelect && onSelect(c)}>
        <div className="d-flex flex-row align-items-center gap-1 flex-wrap">
          <h6 className={`smallFont fw-bold text-${active ? 'white' : 'dark'} mb-0`}>
            {c['boardName'] || c['name'] || translate('no_name')}
          </h6>
          {label && (
            <span
              className="tiny  bg-white d-inline-flex align-items-center justify-content-center ms-auto flex-shrink-0"
              style={{
                padding: '2px 3px',
                fontWeight: 'bold',
                color: colorCodeBasedOnLabel
              }}
            >
              {label}
            </span>
          )}
        </div>
        <h6 className={`tiny text-${active ? 'white' : 'muted'} mb-0`}>{c['email'] || translate('no_email')}</h6>

        <RelatedApartmentStatus relatedApartments={c?.relatedApartments} apartmentProfiles={c?.apartmentProfiles} />
      </div>

      {showProfiles && (
        <>
          <div className="d-flex">
            {c?.apartmentProfiles?.map((apartmentProfile, index) => {
              const matchingProfilePayload = {
                customer: c,
                apartmentProfile,
                profile: `P${apartmentProfile?.index}`
              };
              const handleMatchingProfileChipEnter =
                onMatchingProfileMouseEnter && (() => onMatchingProfileMouseEnter(matchingProfilePayload));
                
              return (
                <div
                  key={apartmentProfile._id}
                  className={`d-flex gap-1 py-1 ${
                    onSelectMatchingCustomers ? 'border border-1 rounded-1 border-grey' : ''
                  }`}
                >
                  <Button
                    key={apartmentProfile._id}
                    size="sm"
                    className="d-flex justify-content-center align-items-center mx-1"
                    style={{ width: 35 }}
                    variant={
                      selectedProfile === apartmentProfile?._id || apartmentProfile?._id === activeMatchingProfileId
                        ? 'dark'
                        : 'outline-dark'
                    }
                    onMouseEnter={handleMatchingProfileChipEnter}
                    onMouseLeave={onMatchingProfileMouseLeave}
                    onClick={() => {
                      if (onMatchingProfileClick) {
                        onMatchingProfileClick(matchingProfilePayload);
                      } else {
                        setSelectedProfile(selectedProfile === apartmentProfile?._id ? null : apartmentProfile?._id);
                        if (!active) {
                          onSelect(c, apartmentProfile?._id);
                        }
                      }
                    }}
                  >
                    <h6 className="smallFont mb-0">P{index + 1}</h6>
                  </Button>
                  {onSelectMatchingCustomers && (
                    <Button
                      size="sm"
                      className="d-flex justify-content-center align-items-center mx-1"
                      style={{ width: 35 }}
                      variant={
                        selectedMatchingCustomers?.some(customer => customer?._id === c?._id)
                          ? hoveredId === c?._id
                            ? 'outline-danger'
                            : 'success text-white'
                          : 'outline-dark'
                      }
                      onClick={() => onSelectMatchingCustomers(c, apartmentProfile?._id)}
                      onMouseEnter={() => setHoveredId(c?._id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <h6 className="smallFont mb-0">
                        {selectedMatchingCustomers?.some(customer => customer?._id === c?._id) ? (
                          hoveredId === c?._id ? (
                            <X size={16} />
                          ) : (
                            <Check2 size={16} />
                          )
                        ) : (
                          <Plus size={16} />
                        )}
                      </h6>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </ListGroup.Item>
  );
};

const CustomerList = ({
  selectedStatus = 'All',
  setSelectedStatus,
  customers = [],
  userList = [],
  selectedUsers = null,
  onUserFilterChange,
  showUserFilter = false,
  activeId = '',
  onSelect,
  quickSearch = true,
  statusFilterInQuickSearch = true,
  onHoverInactive, // New prop
  hoverDelay = 1000, // New prop with default delay of 1000ms (1 second)
  onMatchingProfileClick,
  onMatchingProfileMouseEnter,
  onMatchingProfileMouseLeave,
  activeMatchingProfileId,
  selectedMatchingCustomers,
  onSelectMatchingCustomers,
  showProfiles = false,
  loadingFilteredList = false
}) => {
  const [query, setQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const { translate } = useContext(LocalizeContext);
  const [filteredUnarchivedCustomer, setFilteredUnarchivedCustomer] = useState([]);
  const [filteredArchivedCustomer, setFilteredArchivedCustomer] = useState([]);
  const [showArchivedCustomers, setShowArchivedCustomers] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hiddenTotalUserCount = !showUserFilter;
  useEffect(() => {
    setFilteredCustomers(
      customers.filter(c => {
        const filterBySelectedStatus = selectedStatus === 'All' || !selectedStatus || c['status'] === selectedStatus;

        const filterByQuery =
          !query.toLowerCase().trim() ||
          ['name', 'email', 'boardName'].some(property =>
            c[property]
              ?.toLowerCase()
              .trim()
              .includes(query.toLowerCase().trim())
          );

        const filterFreeIrrCalc = selectedStatus === 'All' ? c.status !== STATUS_FREE_IRR_CALC : true;

        return filterBySelectedStatus && filterByQuery && filterFreeIrrCalc;
      })
    );
  }, [query, customers, selectedStatus]);

  useEffect(() => {
    setFilteredArchivedCustomer(filteredCustomers.filter(c => c.archived));
    setFilteredUnarchivedCustomer(filteredCustomers.filter(c => !c.archived));
  }, [filteredCustomers]);

  useEffect(() => {
    let hoverTimeout;
    if (!isHovering && onHoverInactive) {
      hoverTimeout = setTimeout(onHoverInactive, hoverDelay);
    }

    return () => clearTimeout(hoverTimeout);
  }, [isHovering, onHoverInactive, hoverDelay]);

  return (
    <div className="p-2" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
      <div className="d-flex justify-content-end w-100 mb-2">
        {showUserFilter && userList?.length > 0 && onUserFilterChange && (
          <SearchableFilterDropdown
            optionList={userList}
            selectedData={selectedUsers}
            onFilterChange={onUserFilterChange}
            placeholder={translate('search_users')}
            allItemsLabel={translate('all_users')}
            valueKey="_id"
            labelKey="name"
            count={filteredCustomers?.length}
          />
        )}
      </div>

      {quickSearch && (
        <div className="d-flex flex-row align-items-center gap-1 mb-2">
          <div className="flex-grow-1 min-w-0">
            <TextInput size="sm" hint={translate('quick_search')} onChange={e => setQuery(e.target.value)} />
          </div>
          {statusFilterInQuickSearch && (
            <SearchableFilterDropdown
              optionList={['All', 'New', 'Active', 'Past', STATUS_INTEREST_IN_MY_COURSE, STATUS_FREE_IRR_CALC].map(
                status => ({
                  _id: status,
                  label: status
                })
              )}
              selectedData={
                selectedStatus ? { _id: selectedStatus, label: selectedStatus } : { _id: 'All', label: 'All' }
              }
              onFilterChange={item => setSelectedStatus(item._id)}
              showSearch={false}
              toggleContent={<Pin className="mr-3 align-text-bottom" />}
              valueKey="_id"
              labelKey="label"
            />
          )}
        </div>
      )}

      {/* Total User Count */}
      {hiddenTotalUserCount && (
        <div className="mb-2 px-2 py-1 bg-light rounded">
          <p className="mb-0 text-xs">
            <strong>
              {selectedStatus} Users ({filteredCustomers.length})
            </strong>
          </p>
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {filteredArchivedCustomer.length > 0 && (
          <>
            <div className="d-flex mb-1 mx-1 align-items-center hover-light">
              <div>{showArchivedCustomers ? <ChevronUp size={8} /> : <ChevronDown size={8} />}</div>
              <div className="flex-grow-1 mx-1">
                <p className="mb-0 tiny text-primary" onClick={() => setShowArchivedCustomers(!showArchivedCustomers)}>
                  {showArchivedCustomers
                    ? translate('hide_archived_customers')
                    : translate('show_archived_customers')}{' '}
                  <b>({filteredArchivedCustomer.length})</b>
                </p>
              </div>
            </div>
            {showArchivedCustomers && (
              <ListGroup className="mb-3">
                {filteredArchivedCustomer.map(c => (
                  <CustomerItem
                    key={c['_id']}
                    customer={c}
                    onSelect={onSelect}
                    active={Array.isArray(activeId) ? activeId?.includes(c?._id) : activeId === c['_id']}
                    translate={translate}
                    archived
                    onMatchingProfileClick={onMatchingProfileClick}
                    onMatchingProfileMouseEnter={onMatchingProfileMouseEnter}
                    onMatchingProfileMouseLeave={onMatchingProfileMouseLeave}
                    activeMatchingProfileId={activeMatchingProfileId}
                    selectedMatchingCustomers={selectedMatchingCustomers}
                    onSelectMatchingCustomers={onSelectMatchingCustomers}
                    showProfiles={showProfiles}
                  />
                ))}
              </ListGroup>
            )}
          </>
        )}
        <ListGroup>
          {filteredUnarchivedCustomer.length > 0 ? (
            filteredUnarchivedCustomer.map(c => (
              <CustomerItem
                key={c['_id']}
                customer={c}
                onSelect={onSelect}
                active={Array.isArray(activeId) ? activeId?.includes(c?._id) : activeId === c['_id']}
                translate={translate}
                onMatchingProfileClick={onMatchingProfileClick}
                onMatchingProfileMouseEnter={onMatchingProfileMouseEnter}
                onMatchingProfileMouseLeave={onMatchingProfileMouseLeave}
                activeMatchingProfileId={activeMatchingProfileId}
                selectedMatchingCustomers={selectedMatchingCustomers}
                onSelectMatchingCustomers={onSelectMatchingCustomers}
                showProfiles={showProfiles}
              />
            ))
          ) : loadingFilteredList ? (
            <h6 className="py-5 text-center border rounded tiny text-muted">{translate('loading')}</h6>
          ) : (
            <h6 className="py-5 text-center border rounded tiny">{translate('no_customers_to_show')}</h6>
          )}
        </ListGroup>
      </div>
    </div>
  );
};

export default CustomerList;
